import {
  BookOpen,
  LayoutDashboard,
  ListTree,
  LogOut,
  Menu,
  PhoneOff,
  ScrollText,
  User as UserIcon,
  Users,
} from "lucide-react";
import { useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { NavLink, useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { useLogout, useUpdateLanguagePreference } from "@/hooks/api/auth";
import { useAuthStore } from "@/stores/auth";
import { apiErrorMessage } from "@/lib/api";
import { cn } from "@/lib/utils";
import kraetdtechLogo from "@/assets/kraetdtech-logo.png";
import type { LanguagePreference, Role } from "@/types/api";

interface NavItem {
  to: string;
  icon: typeof LayoutDashboard;
  label: string;
  /** Roles that should see this item. Omit = visible to everyone authed. */
  roles?: Role[];
}

function NavLinks({
  items,
  onNavigate,
}: {
  items: NavItem[];
  onNavigate?: () => void;
}) {
  return (
    <>
      {items.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end
          onClick={onNavigate}
          className={({ isActive }) =>
            cn(
              "flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition-colors duration-150 [-webkit-tap-highlight-color:transparent]",
              isActive
                ? "bg-primary-subtle text-primary"
                : "text-slate-700 hover:bg-surface-alt"
            )
          }
        >
          <item.icon className="h-5 w-5 shrink-0" aria-hidden="true" />
          <span>{item.label}</span>
        </NavLink>
      ))}
    </>
  );
}

function navItems(
  role: Role | null,
  t: (key: string) => string
): NavItem[] {
  return [
    // Admin manages scripts from /flows (list + step editor); Staff browse/play
    // published scripts lands here (FlowListPage).
    { to: role === "ADMIN" ? "/flows" : "/", icon: LayoutDashboard, label: t("appShell.flows") },
    {
      to: role === "ADMIN" ? "/guides/manage" : "/guides",
      icon: BookOpen,
      label: t("appShell.guides"),
    },
    { to: "/staff", icon: Users, label: t("appShell.staff"), roles: ["ADMIN"] },
    { to: "/categories", icon: ListTree, label: t("appShell.categories"), roles: ["ADMIN"] },
    { to: "/call-log", icon: PhoneOff, label: t("appShell.callLog"), roles: ["ADMIN"] },
    { to: "/audit-log", icon: ScrollText, label: t("appShell.auditLog"), roles: ["ADMIN"] },
  ];
}

/** Compact language-preference switcher — reads/writes `user.language_preference`
 * directly (self-service, every role). Changing it updates the UI chrome
 * language immediately (see App.tsx) and how flow content is shown. */
function LanguageSwitcher() {
  const { t } = useTranslation();
  const user = useAuthStore((s) => s.user);
  const updateLanguage = useUpdateLanguagePreference();

  if (!user) return null;

  const handleChange = async (value: string) => {
    try {
      await updateLanguage.mutateAsync(value as LanguagePreference);
    } catch (err) {
      toast.error(apiErrorMessage(err, "Couldn't update your language preference."));
    }
  };

  return (
    <Select value={user.language_preference} onValueChange={handleChange}>
      <SelectTrigger
        className="h-8 w-auto gap-1.5 border-none bg-transparent px-2 text-sm text-slate-600 hover:bg-surface-alt"
        aria-label={t("appShell.language")}
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="EN">{t("appShell.languageEn")}</SelectItem>
        <SelectItem value="ES">{t("appShell.languageEs")}</SelectItem>
        <SelectItem value="BOTH">{t("appShell.languageBoth")}</SelectItem>
      </SelectContent>
    </Select>
  );
}

interface AppShellProps {
  children: ReactNode;
  /** Edge-to-edge main content, no padding. Unused today (the script
   * editor moved off a canvas and back onto the normal padded layout) —
   * kept for any future page that genuinely needs the full viewport. */
  fullBleed?: boolean;
}

export function AppShell({ children, fullBleed = false }: AppShellProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const role = useAuthStore((s) => s.role);
  const logout = useLogout();
  const [navOpen, setNavOpen] = useState(false);

  const handleLogout = async () => {
    setNavOpen(false);
    await logout.mutateAsync();
    navigate("/login", { replace: true });
  };

  const visibleNav = navItems(role, t).filter(
    (item) => !item.roles || (role ? item.roles.includes(role) : false)
  );

  const logoutButton = (
    <Button
      variant="ghost"
      onClick={handleLogout}
      loading={logout.isPending}
      aria-label={t("appShell.logout")}
      className="w-full justify-start gap-3 text-slate-700"
    >
      <LogOut className="h-5 w-5" aria-hidden="true" />
      <span>{t("appShell.logout")}</span>
    </Button>
  );

  // Below the logout button in both nav surfaces (desktop sidebar + mobile
  // drawer) — app-level chrome, not a page element, so it lives here rather
  // than in any individual page. Links out to the company site; opens in a
  // new tab so signing out of the site doesn't also lose the CSR's place
  // mid-call.
  const brandingFooter = (
    <a
      href="https://get.kraetd.com"
      target="_blank"
      rel="noopener noreferrer"
      className="block px-1 opacity-70 transition-opacity duration-150 [-webkit-tap-highlight-color:transparent] hover:opacity-100"
    >
      <img
        src={kraetdtechLogo}
        alt="KraetdTech Industries"
        width={480}
        height={182}
        className="h-auto w-full max-w-[168px]"
      />
    </a>
  );

  return (
    <div className="min-h-screen bg-surface-page">
      <div>
        {/* Desktop sidebar — fixed; width reserved on main via `md:ml-56`. */}
        <aside className="fixed inset-y-0 left-0 z-30 hidden w-56 flex-col border-r border-slate-200 bg-surface md:flex">
          <div className="flex h-14 shrink-0 items-center border-b border-slate-200 px-4">
            {/* Not an <h1> — each page supplies its own content heading;
             * this is chrome/branding, not page content. */}
            <p className="m-0 text-lg font-semibold text-slate-900">agent-spiels</p>
          </div>
          <nav className="flex-1 space-y-1 overflow-y-auto p-2">
            <NavLinks items={visibleNav} />
          </nav>
          <div className="shrink-0 space-y-2 border-t border-slate-200 p-2">
            {logoutButton}
            {brandingFooter}
          </div>
        </aside>

        {/* Mobile nav drawer */}
        <Sheet open={navOpen} onOpenChange={setNavOpen}>
          <SheetContent side="left" className="flex w-72 flex-col p-0">
            <div className="flex h-14 shrink-0 items-center border-b border-slate-200 px-4">
              <SheetTitle>agent-spiels</SheetTitle>
            </div>
            <nav className="flex-1 space-y-1 overflow-y-auto p-2">
              <NavLinks items={visibleNav} onNavigate={() => setNavOpen(false)} />
            </nav>
            <div className="shrink-0 space-y-2 border-t border-slate-200 p-2">
              {logoutButton}
              {brandingFooter}
            </div>
          </SheetContent>
        </Sheet>

        {/* Main */}
        <div className="min-w-0 md:ml-56">
          <header className="sticky top-0 z-20 border-b border-slate-200 bg-surface">
            <div className="flex h-14 items-center justify-between gap-2 px-4 md:px-6">
              <div className="flex min-w-0 items-center gap-2">
                <Button
                  variant="ghost"
                  size="icon"
                  className="-ml-2 md:hidden"
                  onClick={() => setNavOpen(true)}
                  aria-label={t("appShell.openMenu")}
                >
                  <Menu className="h-5 w-5" aria-hidden="true" />
                </Button>
                <span className="truncate font-semibold text-slate-900 md:hidden">
                  agent-spiels
                </span>
              </div>
              <div className="flex min-w-0 items-center gap-1 text-sm text-slate-700">
                <LanguageSwitcher />
                <UserIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
                <span className="hidden max-w-xs truncate sm:inline">{user?.email}</span>
                {role && <Badge tone="primary">{t(`role.${role}`)}</Badge>}
              </div>
            </div>
          </header>

          <main className={fullBleed ? "h-[calc(100vh-3.5rem)]" : "px-4 py-6 md:px-6 md:py-8"}>
            {children}
          </main>
        </div>
      </div>
    </div>
  );
}
